// src/Tools/Utils/chunkErrorHandler.ts
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_WINDOW = 30000; // 30 seconds
const STORAGE_KEY = 'chunk-reload-attempts';

interface ReloadAttempts {
	count: number;
	timestamp: number;
}

function getReloadAttempts(): ReloadAttempts {
	try {
		const data = sessionStorage.getItem(STORAGE_KEY);
		if (!data) return { count: 0, timestamp: Date.now() };

		const parsed = JSON.parse(data);
		const now = Date.now();

		if (now - parsed.timestamp > RELOAD_WINDOW) {
			return { count: 0, timestamp: now };
		}

		return parsed;
	} catch {
		return { count: 0, timestamp: Date.now() };
	}
}

function incrementReloadAttempts(): number {
	const attempts = getReloadAttempts();
	attempts.count += 1;
	attempts.timestamp = Date.now();

	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
	} catch (e) {
		console.error('Failed to save reload attempts:', e);
	}

	return attempts.count;
}

export function handleChunkError(): void {
	const attempts = incrementReloadAttempts();

	if (attempts > MAX_RELOAD_ATTEMPTS) {
		console.error('Max chunk reload attempts reached. Please manually refresh the page.');
		return;
	}

	console.log(`Chunk load error detected. Reloading... (Attempt ${attempts}/${MAX_RELOAD_ATTEMPTS})`);

	const forceReload = () => {
		const url = new URL(window.location.href);
		url.searchParams.set('_v', Date.now().toString());
		window.location.replace(url.toString());
	};

	if ('caches' in window) {
		caches
			.keys()
			.then(names => Promise.all(names.map(name => caches.delete(name))))
			.then(() => forceReload())
			.catch(() => forceReload());
	} else {
		forceReload();
	}
}

export function isChunkLoadError(error: any): boolean {
	if (!error) return false;
	const message = typeof error === 'string' ? error : error?.message || error?.name || error?.toString?.() || '';
	return (
		/Loading chunk/i.test(message) ||
		/ChunkLoadError/i.test(message) ||
		/Loading CSS chunk/i.test(message) ||
		/Unexpected token '<'/i.test(message) ||
		/Failed to fetch dynamically imported module/i.test(message) ||
		/error loading dynamically imported module/i.test(message)
	);
}

export function initChunkErrorHandler(): void {
	window.addEventListener(
		'error',
		event => {
			const isChunk =
				isChunkLoadError(event.error) ||
				isChunkLoadError(event.message) ||
				(event.filename && /\.chunk\.js$/.test(event.filename));

			if (isChunk) {
				event.preventDefault();
				handleChunkError();
			}
		},
		true
	);

	window.addEventListener('unhandledrejection', event => {
		const isChunk = isChunkLoadError(event.reason);
		if (isChunk) {
			event.preventDefault();
			handleChunkError();
		}
	});

	window.addEventListener('load', () => {
		try {
			const url = new URL(window.location.href);
			if (url.searchParams.has('_v')) {
				url.searchParams.delete('_v');
				window.history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
			}
			setTimeout(() => {
				sessionStorage.removeItem(STORAGE_KEY);
			}, 5000);
		} catch (e) {
			// Ignore
		}
	});
}
